import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Checkbox, FormControlLabel } from '@mui/material';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';

import Div from '@jumbo/shared/Div';
import ContentWrapper from 'app/components/ContentWrapper/ContentWrapper';
import ButtonStyled from 'app/components/ButtonStyled/ButtonStyled';
import { reqOtp, verifyOtp } from 'app/services/mfaService';
import { setMfaCookies, getOrCreateUid, clearUidCookie } from 'app/utils/mfaCookies';
import { clearUserSession } from '@jumbo/components/JumboAuthProvider/authHelpers';
import Swal from 'sweetalert2';
import { getBrowserInfo } from 'app/utils/browserInfo';
import { useWindowPaste } from 'app/hooks/useWindowPaste';
import { ERROR_SERVER_DEFAULT } from 'app/utils/appHelpers';

const TOTAL_INPUTS = 6;
const DEFAULT_TTL_SECONDS = 300;

// Resets on page refresh (JS re-executes), persists across remounts within same session.
// Used to prevent a second reqOtp call when JumboAuthProvider redirects back to this page.
let reqOtpCalledThisSession = false;

const SUBTITLE_BY_TYPE = {
  email: 'OTP telah dikirimkan ke Email yang terdaftar pada Referral Booster',
  sms: 'OTP telah dikirimkan ke nomor SMS yang terdaftar pada Referral Booster',
  whatsapp: 'OTP telah dikirimkan ke nomor WhatsApp yang terdaftar pada Referral Booster',
};

const VerifyMfa = () => {
  const navigate = useNavigate();
  const pasteValue = useWindowPaste();

  const userdata = useMemo(() => JSON.parse(localStorage.getItem('userdata') || '{}'), []);
  const identifier = userdata.email;
  const username = userdata.name;
  const channel = localStorage.getItem('mfa_channel') || 'email';

  const [otp, setOtp] = useState(new Array(TOTAL_INPUTS).fill(''));
  const [trustDevice, setTrustDevice] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [locked, setLocked] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [isInitializing, setIsInitializing] = useState(true);
  const otpRefs = useRef(new Array(TOTAL_INPUTS).fill(0).map(() => React.createRef()));

  useEffect(() => {
    if (!localStorage.getItem('mfa_pending')) {
      navigate('/micrositeLogin', { replace: true });
      return;
    }
    otpRefs.current[0]?.current?.focus();

    if (!reqOtpCalledThisSession) {
      reqOtpCalledThisSession = true;
      callReqOtp();
    } else {
      // URL injection remount — reqOtp already in flight or done, restore from stored TTL
      const stored = localStorage.getItem('mfa_ttl_exp');
      const remaining = stored ? Math.max(0, Math.round((Number.parseInt(stored, 10) - Date.now()) / 1000)) : 0;
      setSecondsLeft(remaining);
      setIsInitializing(false);
    }
  }, []);

  useEffect(() => {
    if (secondsLeft <= 0) return undefined;
    const interval = setInterval(() => {
      setSecondsLeft(prev => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [secondsLeft]);

  useEffect(() => {
    if (pasteValue && /^\d{6}$/.test(pasteValue)) {
      setOtp(pasteValue.split(''));
      otpRefs.current[TOTAL_INPUTS - 1]?.current?.focus();
    }
  }, [pasteValue]);

  const handleChange = (value, idx) => {
    if (String(value).length > 1) return;
    const next = [...otp];
    next[idx] = value;
    setOtp(next);
    setError('');
    if (value && idx < TOTAL_INPUTS - 1) {
      otpRefs.current[idx + 1]?.current?.focus();
    }
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === 'Backspace' && !otp[idx] && idx > 0) {
      otpRefs.current[idx - 1]?.current?.focus();
    }
    if (e.keyCode === 69) e.preventDefault();
  };

  const handleBlocked = async (message = "") => {
    reqOtpCalledThisSession = false;
    clearUidCookie(identifier);
    clearUserSession();
    await Swal.fire({
      icon: 'warning',
      title: message === "" ? 'Terlalu banyak percobaan' : message,
      text: 'Silakan coba lagi.',
      confirmButtonColor: '#C21B17',
      allowOutsideClick: false,
    });
    navigate('/micrositeLogin', { replace: true });
  };


  const handleLimitOTP = async (message) => {
    await Swal.fire({
      icon: 'warning',
      title: 'Limit pengiriman OTP',
      text: message,
      confirmButtonColor: '#C21B17',
      allowOutsideClick: false,
    });
    reqOtpCalledThisSession = false;
    clearUserSession();
    navigate('/micrositeLogin', { replace: true });
  };

  const callReqOtp = async () => {
    try {
      const uid = getOrCreateUid(identifier);
      const response = await reqOtp({ identifier, username, type: channel, uid });
      const data = response?.data || {};
      if (data.status === 'Bypassed') {
        proceedAfterMfa();
        return;
      }

      const ttl = Number.parseInt(data?.data?.TTL, 10) || DEFAULT_TTL_SECONDS;
      if (data.status === 'Blocked') {
        handleLimitOTP(data.message);
        return;
      }
      localStorage.setItem('mfa_ttl_exp', String(Date.now() + ttl * 1000));
      setSecondsLeft(ttl);
      setError('');
    } catch (e) {
      setError(e?.message || ERROR_SERVER_DEFAULT);
    } finally {
      setIsInitializing(false);
    }
  };

  const onResend = async () => {
    if (secondsLeft > 0) return;
    await callReqOtp();
  };

  const onSubmit = async () => {
    if (otp.some((d) => !d) || submitting /* || locked */) return;
    setSubmitting(true);
    setError('');
    try {
      const uid = getOrCreateUid(identifier);
      const response = await verifyOtp({
        identifier,
        username,
        type: channel,
        otpCode: otp.join(''),
        uid,
        trustDevice,
        detailInfo: getBrowserInfo(),
      });
      const data = response?.data || {};

      if (data.status === 'Bypassed') {
        // MFA service is down — skip MFA silently
        proceedAfterMfa();
        return;
      }
      if (data.status === 'Blocked') {
        handleBlocked();
        return;
      }
      if (data.status !== 'Ok') {
        const message = data.message || 'OTP Tidak Valid';
        setError(message);
        setOtp(new Array(TOTAL_INPUTS).fill(''));
        otpRefs.current[0]?.current?.focus();
        if (message === 'OTP Sudah Expired') {
          setSecondsLeft(0);
          handleBlocked(message);
        }
        return;
      }

      const token = data?.data?.token;
      if (token) {
        setMfaCookies(identifier, { token, identifier, trustDevice });
      }
      localStorage.removeItem('mfa_channel');
      localStorage.removeItem('mfa_ttl_exp');
      proceedAfterMfa();
    } catch (e) {
      setError(e?.message || ERROR_SERVER_DEFAULT);
    } finally {
      setSubmitting(false);
    }
  };

  const proceedAfterMfa = () => {
    const next = localStorage.getItem('post_mfa_next') || '/micrositeNews';
    localStorage.removeItem('post_mfa_next');
    reqOtpCalledThisSession = false;
    localStorage.removeItem('mfa_pending');
    navigate(next);
  };

  const applyLock = (data) => {
    setLocked(true);
    const ttl = data?.data?.TTL || 0;
    setSecondsLeft(ttl);
    setError(data.message || 'Akun diblokir sementara.');
  };

  const subtitle = SUBTITLE_BY_TYPE[channel] || SUBTITLE_BY_TYPE.email;
  const submitDisabled = otp.some((d) => !d) || submitting /* || locked */;

  return (
    <Div className="custompage">
      <ContentWrapper>
        <Div className="content-padding">
          <Div className="back-icon">
            <KeyboardBackspaceIcon onClick={() => navigate(-1)} />
          </Div>
          <Div className="flex-justify-center-title">
            <Typography fontSize={24} fontWeight={600} color={'#C21B17'}>
              MFA OTP Validation
            </Typography>
          </Div>

          <Div sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <Typography fontSize={14} fontWeight={600} textAlign="center" sx={{ maxWidth: 420 }}>
              {subtitle}
            </Typography>
          </Div>

          <Div sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            {otp.map((value, idx) => (
              <input
                key={`otp-input-${idx}-${value}`}
                ref={otpRefs.current[idx]}
                aria-label={`Digit ${idx + 1} of ${TOTAL_INPUTS}`}
                value={value}
                /* disabled={locked} */
                onChange={(e) => {
                  if (e.target.value === 'e') return;
                  if (e.nativeEvent.inputType !== 'insertFromPaste') {
                    handleChange(e.target.value, idx);
                  }
                }}
                onKeyDown={(e) => handleKeyDown(e, idx)}
                type="number"
                maxLength="1"
                style={{
                  width: 40,
                  height: 40,
                  maxHeight: 40,
                  textAlign: 'center',
                  fontSize: 20,
                  margin: 6,
                  borderRadius: 6,
                  borderColor: '#eee',
                }}
              />
            ))}
          </Div>

          <Div sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={trustDevice}
                  onChange={(e) => setTrustDevice(e.target.checked)}
                  /* disabled={locked} */
                  sx={{ color: '#C21B17', '&.Mui-checked': { color: '#C21B17' } }}
                />
              }
              label={
                <Typography fontSize={13} fontWeight={500}>
                  Ijinkan penggunaan browser ini selama 90 hari
                </Typography>
              }
            />
          </Div>

          <Div sx={{ display: 'flex', justifyContent: 'center', mt: 1 }} aria-live="polite">
            {isInitializing ? null : secondsLeft > 0 ? (
              <Typography fontSize={13} color="#C21B17">
                Silakan menunggu selama <strong>{formatMmSs(secondsLeft)}</strong> detik untuk kirim ulang OTP
              </Typography>
            ) : (
              <Typography
                fontSize={13}
                color="#C21B17"
                sx={{ cursor: 'pointer', /* locked ? 'not-allowed' : */ textDecoration: 'underline' }}
                onClick={onResend}
              >
                Kirim Ulang OTP
              </Typography>
            )}
          </Div>

          {error ? (
            <Div sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
              <Typography fontSize={12} color="#C21B17">
                {error}
              </Typography>
            </Div>
          ) : null}

          <Div className="flex-justify-center" sx={{ mt: 2 }}>
            <ButtonStyled
              disabled={submitDisabled}
              onClick={onSubmit}
              btnText={'Kirim'}
              preset={'primary'}
              sx={{ maxWidth: '451px' }}
              aria-disabled={submitDisabled}
            />
          </Div>
        </Div>
      </ContentWrapper>
    </Div>
  );
};


function formatMmSs(totalSeconds) {
  const mm = Math.floor(totalSeconds / 60);
  const ss = totalSeconds % 60;
  return `${mm}:${ss.toString().padStart(2, '0')}`;
}

export default VerifyMfa;
